import React, { useState } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { AppIconName, SidebarNavIcon } from '../../icons/appIcons.tsx';
import LogoMark from '../common/LogoMark.tsx';

interface User {
  isSuperAdmin?: boolean;
  adminPermissions?: Array<{
    permissionKey: string;
    canRead: boolean;
  }>;
  [key: string]: any;
}

interface AdminSidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout: () => void;
  user?: User | null;
}

type MenuItem = { id: string; label: string; icon: AppIconName };

export default function AdminSidebar({ currentView, onNavigate, onLogout, user }: AdminSidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const isActive = (view: string) => currentView === view;

  const hasPermission = (sectionId: string) => {
    if (user?.isSuperAdmin) return true;
    if (user?.adminPermissions) {
      return user.adminPermissions.some(p => p.permissionKey === sectionId);
    }
    return false;
  };

  const handleNavigate = (view: string) => {
    onNavigate(view);
    setMobileMenuOpen(false);
  };

  const menuSections: { title: string; items: MenuItem[] }[] = [
    {
      title: 'Member & Ranch Management',
      items: [
        { id: 'families', label: 'Manage Families', icon: 'families' },
        { id: 'ranchgirls', label: 'Ranch Girl Profiles', icon: 'ranchGirl' },
        { id: 'horses', label: 'Manage Horses', icon: 'horse' },
        { id: 'horse-reports', label: 'Horse Reports', icon: 'horseReports' },
        { id: 'boardmembers', label: 'Board Members', icon: 'board' },
        { id: 'alumni', label: 'Associated Members', icon: 'members' },
        { id: 'duties', label: 'Duty List', icon: 'duties' }
      ]
    },
    {
      title: 'Communication',
      items: [
        { id: 'events', label: 'Events & Calendar', icon: 'events' },
        { id: 'notifications', label: 'Send Notifications', icon: 'notifications' },
        { id: 'girlscorner', label: 'Girls\' Corner', icon: 'girlsCorner' },
        { id: 'textmessaging', label: 'Text Messaging', icon: 'textMessage' },
        { id: 'documents', label: 'Document Library', icon: 'documents' }
      ]
    },
    {
      title: 'Financial Management',
      items: [
        { id: 'invoices', label: 'Manage Invoices', icon: 'invoices' },
        { id: 'reports', label: 'Financial Reports', icon: 'reports' }
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'user-activity', label: 'User Activity', icon: 'userActivity' },
        ...(user?.isSuperAdmin ? [{ id: 'admin-management', label: 'Admin Management', icon: 'adminUsers' as AppIconName }] : []),
        { id: 'settings', label: 'Admin Settings', icon: 'settings' }
      ]
    }
  ];

  const logoBlock = (
    <>
      {!logoFailed ? (
        <img
          src="/images/ggaa-logo.png"
          alt="GGAA Logo"
          className="h-8 w-8 lg:h-10 lg:w-10 rounded-lg bg-white p-1 shrink-0"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <LogoMark className="h-8 w-8 lg:h-10 lg:w-10" />
      )}
    </>
  );

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-ggaa-green to-ggaa-dark border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            {logoBlock}
            <div>
              <h1 className="text-sm font-bold text-white">God's Green Acre</h1>
              <p className="text-xs text-white/80">Admin Portal</p>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40 mt-16" onClick={() => setMobileMenuOpen(false)} />
      )}

      <div
        className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:h-screen
        mt-16 lg:mt-0
      `}
      >
        <div className="hidden lg:block bg-gradient-to-r from-ggaa-green to-ggaa-dark p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {logoBlock}
            <div className="flex-1 min-w-0">
              <h1 className="text-sm font-bold text-white truncate">God's Green Acre</h1>
              <p className="text-xs text-white/80">Admin Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <div className="mb-6">
            <button
              onClick={() => handleNavigate('dashboard')}
              className={`group w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('dashboard') ? 'bg-ggaa-green text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SidebarNavIcon name="home" active={isActive('dashboard')} />
              <span>Dashboard Home</span>
            </button>
          </div>

          {menuSections.map((section, idx) => {
            const visibleItems = section.items.filter(item => {
              const permissionKey = item.id === 'horse-reports' ? 'horses' : item.id;
              return hasPermission(permissionKey);
            });
            if (visibleItems.length === 0) return null;

            return (
              <div key={idx} className="mb-6">
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {section.title}
                </h3>
                <div className="space-y-1">
                  {visibleItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.id)}
                      className={`group w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.id) ? 'bg-ggaa-green text-white' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <SidebarNavIcon name={item.icon} active={isActive(item.id)} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onLogout}
            className="w-full text-center px-3 py-2 text-sm text-ggaa-green hover:text-ggaa-dark font-medium transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}
